/**
 * [P3] Team-name gate - the first thing an invited manager sees in the lobby.
 *
 * Replaces the auto-defaulted "{First}'s Team" as the entry point: the name is
 * on every board tile all night, so the manager picks it themselves, before
 * they see anything else. The cap is the BOARD's cap (see TEAM_NAME_MAX_CHARS)
 * so a name can never overflow a tile.
 *
 * Accept doubles as the browser gesture that unblocks lobby music when
 * autoplay was refused - the founder should never have to click twice.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { authHeaders } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A board header cell is two clamped lines of 28px Bebas Neue in the narrowest
// (14-team) column. Twenty characters is what survives that without truncation.
export const TEAM_NAME_MAX_CHARS = 20;

export default function TeamNameGateModal({
  leagueId, leagueName, currentName = "", onAccept, onCancel,
}) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const trimmed = name.trim();
  const remaining = TEAM_NAME_MAX_CHARS - name.length;
  const canAccept = trimmed.length > 0 && name.length <= TEAM_NAME_MAX_CHARS && !saving;

  const submit = async () => {
    if (!canAccept) return;
    setSaving(true);
    setError(null);
    try {
      await axios.post(
        `${API}/leagues/${leagueId}/team-name`,
        { team_name: trimmed },
        { headers: authHeaders() },
      );
      onAccept?.(trimmed);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save your team name.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
      style={{ background: "rgba(6,6,6,0.72)", backdropFilter: "blur(10px)" }}
      data-testid="team-name-gate-overlay"
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 sm:p-8"
        style={{
          background: "#141414",
          border: "1px solid rgba(255,107,0,0.55)",
          boxShadow: "0 0 0 1px rgba(255,107,0,0.18), 0 0 42px rgba(255,107,0,0.28), 0 24px 60px rgba(0,0,0,0.65)",
        }}
        data-testid="team-name-gate-modal"
      >
        <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight text-[#F5F5F5] leading-tight">
          Welcome to the 2026 {leagueName} Draft!
        </h2>
        <p className="font-body text-sm text-[rgba(255,255,255,0.55)] mt-2">
          Please enter your team name.
        </p>

        <div className="mt-6">
          <input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, TEAM_NAME_MAX_CHARS))}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder={currentName || "Your team name"}
            maxLength={TEAM_NAME_MAX_CHARS}
            className="w-full bg-[#0E0E0E] border border-[#2E2E2E] focus:border-[#FF6B00] outline-none rounded-xl px-4 py-3 font-display font-bold text-lg uppercase tracking-wide text-[#F5F5F5] transition-colors"
            data-testid="team-name-gate-input"
          />
          <div className="flex items-center justify-between mt-2">
            <span
              className="font-mono text-[11px] tabular-nums"
              style={{ color: remaining <= 3 ? "#FF6B00" : "rgba(255,255,255,0.40)" }}
              data-testid="team-name-gate-counter"
            >
              {name.length}/{TEAM_NAME_MAX_CHARS}
            </span>
            <span className="font-body text-[11px] text-[rgba(255,255,255,0.35)]">
              Fits the draft board tiles
            </span>
          </div>
        </div>

        {error && (
          <p className="mt-3 font-body text-xs text-[#FF4444]" data-testid="team-name-gate-error">
            {error}
          </p>
        )}

        <div className="mt-7 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 border border-[#333] text-[rgba(255,255,255,0.55)] hover:text-[#F5F5F5] hover:border-[#555] font-body text-sm py-3 rounded-xl transition-colors"
            data-testid="team-name-gate-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canAccept}
            className="flex-1 bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-40 text-white font-display font-black uppercase tracking-wider py-3 rounded-xl transition-all active:scale-95 flex items-center justify-center gap-2"
            data-testid="team-name-gate-accept"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : null}
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
